import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  Database
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeader, checkAuth } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [hasVerified, setHasVerified] = useState(false);

  useEffect(() => {
    // Only run once
    if (hasVerified) return;
    
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) {
        toast.error("No session ID found");
        setVerifying(false);
        setHasVerified(true);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/payments/status/${sessionId}`,
          { headers: getAuthHeader() }
        );
        
        setPaymentData(response.data);
        
        if (response.data.payment_status === 'paid') {
          setVerified(true);
          // Refresh user data to get updated subscription status
          await checkAuth();
          toast.success("Payment successful! Your subscription is now active.");
        } else {
          toast.info("Payment is still being processed");
        }
      } catch (error) {
        toast.error("Failed to verify payment");
      } finally {
        setVerifying(false);
        setHasVerified(true);
      }
    };

    verifyPayment();
  }, [hasVerified]); // Only depend on hasVerified

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        {verifying ? (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 mx-auto animate-spin text-primary" />
            <h1 className="text-2xl font-bold">Verifying Payment</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
          </div>
        ) : verified ? (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-green-500">Payment Successful!</h1>
              <p className="text-muted-foreground mt-2">
                Your subscription is now active. Thank you for subscribing!
              </p>
            </div>

            {paymentData && (
              <div className="bg-card border border-border rounded-lg p-4 text-left">
                <h3 className="font-medium mb-2">Payment Details</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span>${paymentData.amount?.toFixed(2)} {paymentData.currency?.toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className="text-green-500 capitalize">{paymentData.payment_status}</span>
                  </div>
                </div>
              </div>
            )}

            <Button 
              className="gap-2" 
              size="lg"
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-yellow-500/10 flex items-center justify-center">
              <Database className="w-10 h-10 text-yellow-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Payment Processing</h1>
              <p className="text-muted-foreground mt-2">
                Your payment is being processed. This may take a moment.
              </p>
            </div>
            <div className="flex gap-2 justify-center">
              <Button 
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Check Again
              </Button>
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
